/* USB Debug Backend - implement debug interface to serial devices

Debugger Commands are:
?/h/help - display this info
w/where - display the block, file and line number of the current line
s/step - execute one line (step into subroutines)
n/next - execute one line (step over subroutines)
r/return - execute until current subroutine ends (step out of subroutines)
c/cont/continue - continue running program
d/dump - display the local variables
g[0-999] - display block N from the total set of global variables
ba [file] line - add a breakpoint
bd [file] line - remove a breakpoint
bl - display all breakpoints
bc - clear all breakpoints
!<python goes here> - force rest of command line to be interpreted as Python
*/

import { TargetStates, TargetModes, DebugCommands, TargetInterface } from './TargetInterface'
import { editorController } from './EditorControl'
import { validatorController } from './ValidatorControl'
import { Targets } from './Players'
import { getActiveMissionPack } from './content-manager/content-director/content-director-use-cases/getActiveMissionPack'
import { FIRMWARE_VERSIONS } from './device-flasher-dialog/device-firmware-versions'
import { getSerialController } from './SerialCommsController'

/*
  REPL Control-code commands:
    ^A (1) - Enter raw mode (no echo, simple prompt)
    ^B (2) - Exit raw mode
    ^C (3) - Break
    ^D (4) - Finish (submit) input in raw or paste modes | soft-reboot in REPL mode
    ^E (5) - Enter paste mode
*/
const CTL_ENTER_RAW = 1
const CTL_ENTER_RAW_CHR = String.fromCharCode(CTL_ENTER_RAW)
const CTL_EXIT_RAW = 2
const CTL_EXIT_RAW_CHR = String.fromCharCode(CTL_EXIT_RAW)
const CTL_BREAK = 3
const CTL_BREAK_CHR = String.fromCharCode(CTL_BREAK)
const CTL_FINISH = 4
const CTL_FINISH_CHR = String.fromCharCode(CTL_FINISH)

// REPL embedded escape sequences delimiting debugger output
const REPL_ESC_DEBUG = '\xC2'
const REPL_ENTER_DEBUG = '\xAB'
const REPL_EXIT_DEBUG = '\xBB'
const REPL_VAR_PREFIX = '\xB7'
const REPL_EDATA_HDR = '\xc0\xde\xca\xfe'

/* Embedded Data Protocol:
     C0DECAFE + LEN_4bytes_u32 + descriptor_16bytes + data
     -- LEN is big endian
     -- descriptor is object-dependent string (see EdataViewer.js)
*/

// Console filter states
const CF_STATE_ON = 0
const CF_STATE_ON_ESC = 1
const CF_STATE_OFF = 2
const CF_STATE_OFF_ESC = 3
const CF_STATE_NO_ECHO = 4
const CF_STATE_EDAT_HDR = 5
const CF_STATE_EDAT_LEN = 6
const CF_STATE_EDAT_BIN = 7

const TRACEBACK_BUF_SIZE = 500

// Cap for rx-tracking string buffers (commandBuf, accumulatedBuf, varsBuf).
// These only need to retain enough tail to match a token split across serial
// chunks (prompts, boot banner, debugger lines — all well under 1KB). Without
// a cap, a device spewing unmatched output grows them without bound and the
// per-chunk regex scans over the full buffer lock up the browser.
const RX_TRACKING_BUF_SIZE = 16384

// Error message thrown when the device stops answering command prompts during
// download. Signals "stop sending, tell the user to power-cycle".
const DEVICE_UNRESPONSIVE = 'Device unresponsive'

// Error message thrown when a download is deliberately abandoned because the
// user pressed stop. Unlike DEVICE_UNRESPONSIVE this is not an error to report:
// stopRun() is already driving the device back to the REPL.
const DOWNLOAD_INTERRUPTED = 'Download interrupted'

// On device connect: how long to listen for the device's own REPL prompt or
// boot banner before nudging it with a bare CR, and before falling back to the
// stop sequence. Transmitting too early — especially ^C — while MicroPython's
// USB stack (tinyUSB) is still starting up can wedge it beyond even ^C recovery.
const BOOT_NUDGE_DELAY_MS = 1000
const BOOT_REPL_WAIT_MS = 2000

// CodeAIR settle delay (experimental — tune as needed) between the end of the
// code transfer (final flash sync) and the reboot sequence that launches it.
// Rebooting immediately after the sync has been observed to wedge CodeAIR's
// USB (tinyUSB) so hard that only a power-cycle recovers it. NOTE: do NOT add
// a delay AFTER the reboot ^D inside download() — state is LOADING there, all
// rx is dropped, and a fast program's entire run would be invisible.
const CODEAIR_TRANSFER_SETTLE_MS = 1000

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

const tracebackRegEx = /(?:Traceback \(most recent call last\):)$\s+File(?:.*$\s+File)* "(.+)", line (\d+),?.*\r*\n(\S*:.*)\r*\n/gm

const varsRegEx = /(.*):\xc2\xb7(?:\xc2\xab)*(?:\xc2\xbb)*(.*)\xc2\xb7(.*)/
const localsRequestRegEx = /<<< d/
const globalsRequestRegEx = /<<< g(\d+)/
// eslint-disable-next-line
const replPromptRegEx = /\x0A\x3E\x3E\x3E\x20/gm
// eslint-disable-next-line
const debugPromptRegEx = /\x0A\x3C\x3C\x3C\x20/gm
const unableToSetBkptRegEx = /ba (\d+)\s*Not an executable source line/gm
const terminatedExprRegEx = /debugger disarmed/gm

// Globals not to be shown in variables panel
const ignoreVars = ['__file__', '__name__']

// Globals to be inserted under the corresponding library module
const libVars = {
  codex: [
    'GREEN', 'BLACK', 'BLUE', 'RED', 'WHITE', 'BROWN', 'ORANGE', 'CYAN', 'DARK_BLUE', 'DARK_GREEN', 'LIGHT_GRAY', 'MAGENTA',
    'PINK', 'PURPLE', 'remote', 'pixels', 'power', 'YELLOW', 'display', 'exp', 'GRAY', 'lcd', 'leds', 'LED_A', 'LED_B',
    'light', 'BTN_A', 'BTN_B', 'BTN_U', 'BTN_D', 'BTN_L', 'BTN_R', 'buttons', 'accel', 'ALL_COLORS', 'audio', 'backlight',
    'tft', 'COLORS_BY_NAME', 'TRANSPARENT', 'console', 'COLOR_LIST',
  ],
  botcore: ['accel', 'ADC_FULL_SCALE', 'buttons', 'enc', 'exp', 'leds', 'LEFT', 'ls', 'motors', 'prox', 'RIGHT', 'spkr', 'system'],

}

// Library modules per device (where libVars are found)
const findLib = {
  USB_CODEX: 'codex',
  USB_CODEAIR: 'codeair',
  USB_CB2: 'botcore',
  USB_CB3: 'botcore',
}

class VariableWatcher {
  constructor(usb) {
    this.locals = []
    this.globals = []
    this.varsBuf = ''
    this.usb = usb
    this.lastGlobalNum = 0
    this.expectingGlobals = false
    this.globalsChanged = false
    this.localsChanged = false
  }

  check = (buf) => {
    // This function is handed fragmented serial rx data
    this.varsBuf += buf
    if (this.varsBuf.length > RX_TRACKING_BUF_SIZE) {
      // Output with no '\r' (e.g. binary spew) would otherwise accumulate forever
      this.varsBuf = this.varsBuf.slice(-RX_TRACKING_BUF_SIZE)
    }

    // If complete line(s) are found, process line at a time
    if (this.varsBuf.includes('\r')) {
      this.globalsChanged = false
      this.localsChanged = false
      const lastReturn = this.varsBuf.lastIndexOf('\r')
      const lines = this.varsBuf.slice(0, lastReturn + 1).split('\r')
      lines.forEach(line => this.processLine(line))
      this.varsBuf = this.varsBuf.slice(lastReturn + 1)
    }

    // If we're waiting at debug prompt...
    if (this.varsBuf.slice(-4) === '<<< ') {
      // Are there more globals coming? Got full count from prior gX requests; not at g8 limit;
      if (this.globalsChanged && this.lastGlobalNum < 9 && this.globals.length === (this.lastGlobalNum + 1) * 8) {
        this.usb.sendDebuggerCmd(`g${this.lastGlobalNum + 1}\r`, true)
      } else if ((!this.globalsChanged && this.lastGlobalNum < 9 && this.globals.length === this.lastGlobalNum * 8) || this.globalsChanged) {
        const debugInfo = this.normalize()
        this.usb.onDebugChange(debugInfo, true)
      }

      this.globalsChanged = false
      this.localsChanged = false
    }
  }

  normalize = () => {
    // Convert this.locals[] and this.globals[] to filtered object mappings of name:val
    const debugInfo = {
      variables: {
        Locals: {},
        Globals: {},
      },
    }
    if (this.usb.deviceConnected !== Targets.UNCONNECTED) {
      // If a device is connected we automatically insert the 'botcore' or 'codex' lib
      debugInfo.variables.Globals[findLib[this.usb.deviceConnected]] = { pyValType: 'firiaModule', value: {} }
    }
    let i = 0
    // Build object mapping local vars name:val
    for (i = 0; i < this.locals.length; i++) {
      debugInfo.variables.Locals[this.locals[i].name] = this.locals[i].val
    }
    // Build Globals object mapping, also filter 'ignoreVars'
    for (i = 0; i < this.globals.length; i++) {
      if (ignoreVars.includes(this.globals[i].name)) {
      } else if (this.usb.deviceConnected !== Targets.UNCONNECTED && libVars[findLib[this.usb.deviceConnected]].includes(this.globals[i].name)) {
        // Insert members of 'libVars' under the corresponding (botcore or codex) library module
        debugInfo.variables.Globals[findLib[this.usb.deviceConnected]].value[this.globals[i].name] = this.globals[i].val
      } else {
        debugInfo.variables.Globals[this.globals[i].name] = this.globals[i].val
      }
    }
    return debugInfo
  }

  processLine = (line) => {
    const lineExpr = /block=(.*?) file=(.*?) line=(\d+)/
    let m = lineExpr.exec(line)
    if (m && (m[2] === '__main__' || m[2] === 'main.py')) {
      let debugInfo = {
        line: parseInt(m[3], 10),
        name: m[1],
        // file : m[2], // if it is main.py just leave it missing
      }
      this.usb.onDebugChange(debugInfo)
    }

    localsRequestRegEx.lastIndex = 0
    if (localsRequestRegEx.test(line)) {
      this.locals = []
      this.expectingGlobals = false
      this.usb.sendDebuggerCmd('g0\r', true)
    } else {
      globalsRequestRegEx.lastIndex = 0
      m = globalsRequestRegEx.exec(line)
      if (m) {
        this.lastGlobalNum = parseInt(m[1], 10)
        if (this.lastGlobalNum === 0) {
          this.globals = []
        }
        this.expectingGlobals = true
        // this.setMbState(mb.state.RUNNING)
      }
    }

    varsRegEx.lastIndex = 0
    m = varsRegEx.exec(line)
    if (m) {
      let vars = this.locals
      if (this.expectingGlobals) {
        vars = this.globals
        this.globalsChanged = true
      } else {
        this.localsChanged = true
      }

      vars.push({
        name: m[1],
        val: {
          value: m[2],
          pyValType: m[3],
          strValue: m[2].toString(),
        },
      })
    }
  }
}

export default class UsbDebugBackend extends TargetInterface {
  constructor() {
    super()

    this.deviceData = {
      USB_CODEX: {
        bootRegex: /FiriaLabs IoT Python (.*) on ([\d-]*); ([0-9a-fA-F]+).* (CodeX \S*) with (\S*)/gm,
        hwVer: null,
        numBreakpoints: 16,
        intCmd: '\x02\x03', // Exit raw, ^C
        verId: 'codex',
        displayName: 'CodeX',
      },
      USB_CODEAIR: {
        bootRegex: /FiriaLabs Iot Python (.*) on ([\d-]*); Firia Labs CodeAIR with (\S*)/gm,
        hwVer: null,
        numBreakpoints: 0,
        intCmd: '\x02\x03', // Exit raw, ^C
        verId: 'codeair',
        displayName: 'CodeAIR',
      },
      USB_CB2: {
        bootRegex: /MicroPython ([0-9a-fA-F]+).* on ([\d-]*); ([0-9a-fA-F]+).* (\S*) with (\S*)/gm,
        hwVer: null,
        numBreakpoints: 16,
        intCmd: '\x02\x03c\r', // Exit raw, ^C, 'c' to continue debugger, return for prompt
        verId: 'cb2',
        displayName: 'CodeBot',
      },
      USB_CB3: {
        bootRegex: /FiriaLabs IoT Python (.*) on ([\d-]*); ([0-9a-fA-F]+).* (CodeBot \S*) with (\S*)/gm,
        hwVer: null,
        numBreakpoints: 16,
        intCmd: '\x02\x03', // Exit raw, ^C
        verId: 'cb3',
        displayName: 'CodeBot',
      },
    }

    this.getHwVerData()  // Fetch device versions from cloud
    this.varsWatcher = new VariableWatcher(this)

    this.consoleFilterState = CF_STATE_ON
    this.consoleFilterDebugRecurse = 0
    this.bootRegEx = /^/gm

    this.deviceConnected = Targets.UNCONNECTED
    this.deviceVersionInfo = null

    this.onCmdPrompt = null
    this.onCmdFail = null
    this.cmdExpectExpr = null
    this.commandBuf = ''
    this.edataSeq = 0

    this.replSeenOnBoot = false
    this.replWatchBuf = ''
    this.stopRunActive = false
    this.stopRunPromise = null
    this.startPending = false
    this.abortDownload = false
  }

  // Wait out any in-flight stop sequence. run()/debug() call this FIRST —
  // before changing mode/state or transmitting — because a still-active
  // stopRun would otherwise catch the REPL prompt elicited by the new
  // download's own preamble, then stomp mode/state to REPL/STOPPED while the
  // download streams on in raw mode (symptom: 'OK>' acks spilling into the
  // console, and a soft-rebooted program running behind a stopped UI).
  waitForStopRun = async () => {
    while (this.stopRunActive) {
      await this.stopRunPromise
    }
  }

  init = async (target) => {
    this.devicePlatform = target
    if (this.deviceConnected === Targets.UNCONNECTED) {
      this.serialComms = getSerialController(target)
      this.serialComms.dataReceivedCb = this.handleSerialChars
      this.serialComms.deviceConnectedCb = this.onDeviceConnected
      this.serialComms.deviceDisconnectedCb = this.onDeviceDisconnected
      this.serialComms.setTarget(target)
      await this.serialComms.connectAvailable()
    } else {
      this.stopRun()
    }
  }

  deinit = async (target) => {
    this.deviceConnected = Targets.UNCONNECTED
    this.serialComms.dataReceivedCb = null
    this.serialComms.deviceConnectedCb = null
    this.serialComms.deviceDisconnectedCb = null
    await this.serialComms.disconnect()
  }

  pairDevice = async () => {
    await this.serialComms.request()
  }

  getHwVerData = async () => {
    // Fetch device versions from cloud.
    try {
      for (const key in this.deviceData) {
        // First version in list is guaranteed to be the latest version.
        const deviceObj = FIRMWARE_VERSIONS[this.deviceData[key].verId]
        if (!!deviceObj) {
          this.deviceData[key].hwVer = FIRMWARE_VERSIONS[this.deviceData[key].verId][0].sha
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  receiveTextFromTerminal = async (text) => {
    if (text === '\x03') {
      this.stopRun()
    } else {
      // Filter the echo of command we're about to send
      this.consoleFilterState = CF_STATE_NO_ECHO
      this.echoFilter = text + '\r'
      await this.serialComms.send(text + '\r')
      // await this.sendCommand(text + '\r', />>>/, false)
    }
  }

  breakpointChanged = () => {
    if (this.lastTargetState === TargetStates.AWAITING_INPUT && this.targetMode === TargetModes.DEBUG) {
      this.sendAllBreakpoints()
    }
  }

  onModeChange = (mode) => {
    // console.log(`ModeChange: ${TargetModes[this.targetMode]} -> ${TargetModes[mode]}`)
    this.targetMode = mode
    this.invokeExistingCallbacksOnly(this.modeCallbacks, mode)
  }

  endInitializationState = () => {
    if (this.lastTargetState === TargetStates.INITIALIZING) {
      this.onStateChange(TargetStates.STOPPED, true)
    }
  }

  onStateChange = (targetState, initializationOverride) => {
    // console.log(`StateChange: ${TargetStates[this.lastTargetState]} -> ${TargetStates[targetState]}`)
    if (this.lastTargetState === TargetStates.INITIALIZING && !initializationOverride) {
      this.invokeExistingCallbacksOnly(this.stateCallbacks, this.lastTargetState)
      return
    }

    if (this.stateCallbacks.size && targetState !== this.lastTargetState) {
      this.invokeExistingCallbacksOnly(this.stateCallbacks, targetState)
    }
    this.lastTargetState = targetState
  }

  hydrate = () => {
    if (this.lastTargetState === TargetStates.INITIALIZING) {
      this.invokeExistingCallbacksOnly(this.stateCallbacks, this.lastTargetState)
    }
  }

  // { line: int, variables: dictionary }
  onDebugChange = (debugInfo, variablesOnly = false) => {
    if (!variablesOnly && this.targetMode === TargetModes.DEBUG) {
      this.onStateChange(TargetStates.AWAITING_INPUT)
      this.sendDebuggerCmd('d\r', true)
    }

    this.invokeExistingCallbacksOnly(this.debugCallbacks, debugInfo)
  }

  clearConnectionTimeout = () => {
    clearTimeout(this.connectionTo)
    clearTimeout(this.connectionNudgeTo)
  }

  onDeviceConnected = (deviceName) => {
    this.tracebackBuffer = ''
    this.echoFilter = ''
    this.deviceVersionInfo = null
    this.deviceConnected = deviceName || this.devicePlatform
    const deviceInfo = this.deviceData[this.deviceConnected]
    if (!deviceInfo) {
      // Unknown/unconnected target (e.g. a failed reconnect). Bail rather than
      // crashing on undefined.bootRegex.
      console.warn('onDeviceConnected: no device data for', this.deviceConnected)
      return
    }
    this.bootRegEx = deviceInfo.bootRegex
    this.replSeenOnBoot = false
    this.deviceConnCb()
    this.onStateChange(TargetStates.INITIALIZING)

    // Transmit NOTHING yet: interrupting MicroPython's USB startup can wedge it
    // beyond recovery (even ^C stops working; only a power-cycle clears it).
    // Instead watch for the device to speak first — its boot banner or REPL
    // prompt (see trackDebugVsRepl, which finishes initialization on either).
    // After a quiet BOOT_NUDGE_DELAY_MS, coax an idle REPL into showing itself
    // with a bare CR — inert, unlike ^C. Only if the device still hasn't shown
    // a prompt at BOOT_REPL_WAIT_MS do we assume it's running code and send the
    // stop sequence.
    this.connectionNudgeTo = setTimeout(() => {
      if (this.deviceVersionInfo === null && !this.replSeenOnBoot) {
        this.serialComms.send('\r')
      }
    }, BOOT_NUDGE_DELAY_MS)
    this.connectionTo = setTimeout(() => {
      if (this.deviceVersionInfo === null && !this.replSeenOnBoot) {
        // console.log('sending STOP sequence')
        this.endInitializationState()
        this.stopRun()
      }
    }, BOOT_REPL_WAIT_MS)
  }

  onDeviceDisconnected = () => {
    this.deviceConnected = Targets.UNCONNECTED
    this.showedUpgradeNotice = false
    this.deviceVersionInfo = null
    this.clearConnectionTimeout()
    if (this.onCmdFail) {
      // Fail any in-flight sendCommand immediately (e.g. a mid-download unplug)
      // rather than letting it wait out its full timeout.
      const reject = this.onCmdFail
      this.onCmdFail = null
      this.onCmdPrompt = null
      reject('Device disconnected')
    }
    this.endInitializationState()
    this.stopRun()
    this.onModeChange(TargetModes.DISCONNECTED)
    this.deviceConnCb()
  }

  copyPythonFile = async (filename) => {
    // Called after download saves current file as 'main.py'. We want to allow 'import' if the user
    // has named the file properly, so we need to save it by the correct name. This is an MVP approach
    // to multi-file support.
    // Is currentFilename a proper Python filename?
    const match = filename && filename.match(/^[a-zA-Z_]\w*\.py$/)

    if (match) {
      // Copy main.py to proper filename (copy src->dest in chunks)
      // Allow extra time for file copy. Measured about 250ms for 10k file. Conservatively allow 1000ms per 10k.
      const copyTimeout = 4000 + Math.round(this.lastLoadedCode.length / 10)
      await this.sendCommandOrThrow(`d=open("${filename}","w")`)
      await this.sendCommandOrThrow('s=open("main.py")')
      await this.sendCommandOrThrow('while d.write(s.read(512)): pass\r', />/, true, copyTimeout)
      await this.sendCommandOrThrow('d.close()')
    }
  }

  download = async (destFilename, code) => {
    const mainFilename = 'main.py'
    this.abortDownload = false  // Set by stopRun() to abandon this download
    let fileBuf = code
    this.lastLoadedCode = code
    fileBuf = fileBuf.replace(/'{3}/gm, '"""')  // Replace all triple single quotes with triple double quotes
    fileBuf = fileBuf.replace(/\r\n/gm, '\n')

    // Verify we're in stop state; wait for prompt
    let gotRepl = await this.sendCommand('\r', />>>/, false)
    if (!gotRepl) {
      // Retry with the interrupt sequence (exit-raw + ^C): recovers a device
      // stuck in raw mode (e.g. a previously aborted download) or still
      // running user code.
      const intCmd = this.deviceData[this.deviceConnected]?.intCmd || (CTL_EXIT_RAW_CHR + CTL_BREAK_CHR)
      gotRepl = await this.sendCommand(intCmd + '\r', />>>/, false)
    }
    if (!gotRepl) {
      // No REPL prompt — the device isn't answering (or stopRun aborted us).
      // Abort BEFORE streaming code at it: everything sent to a wedged device
      // gets echoed back when it recovers, flooding the console with the
      // whole program.
      throw new Error(this.abortDownload ? DOWNLOAD_INTERRUPTED : DEVICE_UNRESPONSIVE)
    }

    // Open file and init write shortcut
    await this.sendCommandOrThrow(CTL_ENTER_RAW_CHR)
    await this.sendCommandOrThrow(`fd=open("${mainFilename}", "wb")`)
    await this.sendCommandOrThrow('w=fd.write')

    const chunksize = 1024
    for (let i = 0; i < fileBuf.length; i += chunksize) {
      let chunk = fileBuf.substr(i, chunksize)

      chunk = chunk.replace(/\\/g, '\\x5c')  // literal backslash

      if (chunk.slice(-1) === '\'') {
        // If the last character is a single quote then escape it since we are about to wrap it in triple single quotes
        chunk = chunk.slice(0, -1) + '\\\''
      }

      // Using triple single quotes to minimize what needs to be escaped.
      // Abort on the first unacknowledged chunk — a retry could double-write
      // if the device executed the first send but the echo arrived late.
      await this.sendCommandOrThrow(`w('''${chunk}'''.encode('utf-8'))`)
    }

    await this.sendCommandOrThrow('fd.close()')

    // TEST Program Load Failure
    // await this.sendCommand('from time import sleep')
    // await this.sendCommand('sleep(10)')

    if (destFilename !== mainFilename) {
      // Persist file if it's a proper Python filename
      await this.copyPythonFile(destFilename)
    }

    // Reset device
    if (this.devicePlatform === Targets.USB_CB2) {
      // Rewrite boot.py in case of flash corruption. This will no longer be necessary when we:
      // a) Implement multifile support with file-integrity checks, and/or b) Fix flash corruption issues.
      await this.sendCommandOrThrow('fd=open("boot.py", "wb")')
      await this.sendCommandOrThrow('fd.write("import pyb\\nc=None")')
      await this.sendCommandOrThrow('fd.close()')
      await this.sendCommandOrThrow('pyb.sync()')  // Write file to FLASH
    } else {
      // I don't know if the following is necessary or not.
      // I am only doing it because the CB2 code invokes pyb.sync()
      await this.sendCommandOrThrow('from os import sync')
      await this.sendCommandOrThrow('sync()')  // Write file to FLASH
    }

    if (this.devicePlatform === Targets.USB_CODEAIR) {
      // Transfer complete — let the device settle before rebooting it into
      // the new program (see CODEAIR_TRANSFER_SETTLE_MS).
      await delay(CODEAIR_TRANSFER_SETTLE_MS)
    }

    await this.sendCommandOrThrow(CTL_EXIT_RAW_CHR)
    // Soft reboot (may not get prompt if looping). Not awaited: serial writes
    // are queued in order regardless, and a blocked write must not hang load().
    this.serialComms.send(CTL_FINISH_CHR)

    if (this.devicePlatform !== Targets.USB_CODEAIR) {
      // Targets with debugger support will see the debug prompt on restart
      await this.sendCommand('\r', /<<</, false)

      // Set console filter state directly since we will have missed the REPL_ENTER_DEBUG flag.
      this.consoleFilterState = CF_STATE_OFF
      this.consoleFilterDebugRecurse = 0
    }
    // CodeAIR: return immediately — run() must reach the RUNNING state right
    // away, because while state is LOADING all rx is dropped, and a fast
    // program's entire output (including 'Code done running.') would be eaten.
  }

  sendAllBreakpoints = async () => {
    this.breakpoints = editorController.getBreakpoints()
    await this.sendCommand('bc\r', /<<</, false)  // Clear all breakpoints
    for (let i = 0; i < this.breakpoints.length; i++) {
      await this.sendCommand(`ba ${this.breakpoints[i]}\r`, /<<</, false)  // Add breakpoint to 'main.py'
    }
  }

  load = async (filename, code) => {
    this.tracebackBuffer = ''
    this.accumulatedBuf = ''

    // Download and reset to start
    try {
      await this.download(filename, code)
    } catch (error) {
      if (error?.message === DOWNLOAD_INTERRUPTED) {
        // User pressed stop mid-download. stopRun() is already driving the
        // device back to the REPL — no error to report, no cleanup to add.
        throw error
      }
      // Enable specific error handling behavior
      if (error === 'Traceback error') {
        // The traceback error callback is already being invoked, so there is no reason to invoke it here
      } else if (error?.message === DEVICE_UNRESPONSIVE) {
        // Device stopped answering prompts. Tell the user what to do about it —
        // typically the fix is unplug, power-cycle, reconnect.
        const deviceName = this.deviceData[this.deviceConnected]?.displayName || 'Device'
        this.onDebugChange({ deviceUnresponsive: true, deviceName })

        console.log('Download aborted, device unresponsive')
      } else {
        // Display download failed error, only if we don't have a more specific error to display
        const debugInfo = { programLoadFailed: true }
        this.onDebugChange(debugInfo)

        console.log('Download Failed', { error: error, rxBuf: window.firiaSerialRxDebug })
      }
      // ^C may be needed to exit triple-quotes. Not awaited: on a wedged
      // device this write can block indefinitely, which would hang the
      // recovery path itself.
      this.serialComms.send(CTL_BREAK_CHR)
      await this.stopRun() // check that this is the right thing

      // Propagate so run()/debug() don't continue commanding a device that has no program loaded
      throw error
    }
  }

  sendDebuggerCmd = async (cmd, internal = false) => {
    // console.log("sendDebuggerCmd: ", cmd)
    await this.serialComms.send(cmd)
  }

  run = async (filename, code, language) => {
    // TODO check if CB2 is ready to accept commands

    if (this.startPending) {
      return  // A run/debug is already starting; don't launch a second download
    }
    this.startPending = true
    try {
      await this.waitForStopRun()
      this.onModeChange(TargetModes.RUN)
      editorController.clearMarkers()
      // If run is pressed during the post-connect quiet window the device is
      // evidently present: disarm the boot watchers (their stopRun would abort
      // this download) and finish initialization so LOADING can take effect.
      this.clearConnectionTimeout()
      this.endInitializationState()
      this.onStateChange(TargetStates.LOADING)

      await this.load(filename, code)
      if (this.devicePlatform !== Targets.USB_CODEAIR) {
        // Continue cmd runs the code on debugger targets, which halt at the
        // debug prompt after the reboot. CodeAIR has no debugger: auto-reload
        // launches main.py by itself, and a 'c\r' would only inject bytes
        // into the reboot/USB-reinit window (the tinyUSB wedge trigger) or,
        // later, into the running program's stdin. Deliberately not awaited:
        // a backpressure-blocked write (wedged device) would pin this run()
        // — and startPending — forever.
        this.sendDebuggerCmd('c\r')
      }

      this.onStateChange(TargetStates.RUNNING)
    } catch (error) {
      console.error(error)
    } finally {
      this.startPending = false
    }
  }

  promiseTimeout = (ms, promise) => {
    // Return a promise that rejects in <ms> milliseconds
    const timeout = new Promise((resolve, reject) => {
      let id = setTimeout(() => {
        clearTimeout(id)
        reject(`Timeout ${ms}ms : ${promise.info}`)
      }, ms)
    })

    // Returns a race between our timeout and the passed in promise
    return Promise.race([
      promise,
      timeout,
    ])
  }

  handleCommandResponseRx = (buf) => {
    if (!this.onCmdPrompt) {
      // No command awaiting a response. Don't accumulate: a device spewing
      // output between commands would grow the buffer without bound.
      return
    }
    this.commandBuf += buf
    if (this.commandBuf.length > RX_TRACKING_BUF_SIZE) {
      // Keep only the tail — expected prompts are tiny
      this.commandBuf = this.commandBuf.slice(-RX_TRACKING_BUF_SIZE)
    }
    const result = this.cmdExpectExpr.exec(this.commandBuf)
    if (result) {
      this.commandBuf = ''
      const resolve = this.onCmdPrompt
      this.onCmdPrompt = null
      this.onCmdFail = null
      resolve()
    }
  }

  sendCommand = async (cmd, expect = />/, finish = true, timeout = 4000) => {
    this.commandBuf = '' // Reset buffer for expected response
    const sendPromise = new Promise((resolve, reject) => {
      this.cmdExpectExpr = expect
      this.onCmdPrompt = resolve // Called when expected response is received
      this.onCmdFail = reject   // Potentially called if an external condition dictates failure

      // If cmd is a non-control character, append a "finish"
      if (finish && cmd.charCodeAt(0) > 9) {
        cmd += CTL_FINISH_CHR
      }
    })
    // If onCmdFail fires while the physical send below is still blocked,
    // nothing is awaiting sendPromise yet; absorb the rejection so it can't
    // surface as an unhandled-promise error.
    sendPromise.catch(() => {})

    // The physical send must be INSIDE the timeout race: a wedged device stops
    // draining USB, and a backpressure-blocked write never settles — not even
    // on unplug. Outside the race it would hang this promise chain (and the
    // run button) until the device itself is rebooted.
    const sendAndAwait = (async () => {
      await this.serialComms.send(cmd)
      await sendPromise
    })()
    sendAndAwait.info = `sending ${cmd} awaiting ${expect}` // For timeout message

    try {
      await this.promiseTimeout(timeout, sendAndAwait)
      return true // Sent, and expected response received within timeout
    } catch (error) {
      // Timeout (blocked send or no response), send failure, or onCmdFail rejection
      console.log(`sendCommand failed for cmd "${cmd}":`, error)
      return false
    } finally {
      // Clear handlers so a late-arriving prompt can't resolve a dead promise,
      // and so handleCommandResponseRx stops accumulating rx data.
      this.onCmdPrompt = null
      this.onCmdFail = null
    }
  }

  // Same as sendCommand, but treats failure as fatal to the calling operation.
  // Used during download, where continuing to stream code at a device that has
  // stopped answering floods it (and, on recovery, the console) with data.
  sendCommandOrThrow = async (cmd, expect, finish, timeout) => {
    if (this.abortDownload) {
      // stopRun() abandoned this download between commands — send nothing
      // more, so its interrupts can't interleave with our raw-mode stream.
      throw new Error(DOWNLOAD_INTERRUPTED)
    }
    const ok = await this.sendCommand(cmd, expect, finish, timeout)
    if (!ok) {
      // A user-initiated stop also lands here (stopRun rejects the pending
      // command); distinguish it so load() doesn't report a device fault.
      throw new Error(this.abortDownload ? DOWNLOAD_INTERRUPTED : DEVICE_UNRESPONSIVE)
    }
  }


  KeyboardInterrupt = () => {
    if (this.deviceConnected !== Targets.UNCONNECTED) {
      this.sendDebuggerCmd(this.deviceData[this.deviceConnected].intCmd)
    }
  }

  // Respond to the STOP RUNNING button. Returns true if the device answered
  // with a REPL prompt, false if it never did (user is notified in that case).
  stopRun = async () => {
    if (this.targetMode === TargetModes.DEBUG) {
      this.invokeExistingCallbacksOnly(this.debugCallbacks, { line: -1, variables: { Locals: {}, Globals: {} } })
    }

    if (this.deviceConnected === Targets.UNCONNECTED) {
      // Nothing to interrupt (and nothing to wait on)
      this.onStateChange(TargetStates.STOPPED)
      return false
    }

    if (this.stopRunActive) {
      // A stop sequence is already driving the device back to the REPL; a
      // second concurrent one would just interleave ^C traffic with it.
      return false
    }
    this.stopRunActive = true
    let releaseStopRun
    this.stopRunPromise = new Promise((resolve) => {
      releaseStopRun = resolve
    })

    // Abort any in-flight download so its chunk stream can't interleave with
    // the interrupts below (that interleaving wedges the device in raw mode).
    this.abortDownload = true
    if (this.onCmdFail) {
      const reject = this.onCmdFail
      this.onCmdFail = null
      this.onCmdPrompt = null
      reject('Stop requested')
    }

    // CodeBot only requires this sequence, AND experiences FATAL unhandled exception errors when you
    // issue a stop() while single-stepping if you do the full sequence with 'c\r' appended.
    // TODO: Investigate the FATAL error case...

    // Send ^C repeatedly since an unexpected reboot can put device in debug mode for a period of time,
    // especially since 'stopRun' is used in onDeviceConnected() and otherwise can fail to elicit REPL
    const max_retries = 5
    const timeout = 1000  // ms
    let gotRepl = false
    try {
      for (let i = 0; i < max_retries; ++i) {
        if (this.deviceConnected === Targets.UNCONNECTED) {
          break  // Unplugged mid-sequence
        }
        this.KeyboardInterrupt()
        try {
          await (new Promise((resolve, reject) => {
            const tm = setTimeout(reject, timeout)
            this.replWatchBuf = ''
            this.gotReplPrompt = () => {
              // console.log('got REPL!')
              clearTimeout(tm)
              resolve()
            }
          }))
          gotRepl = true
          break  // success!
        } catch (err) {
          // console.log('timeout waiting on REPL, retrying...')
        }
      }
    } finally {
      this.gotReplPrompt = null
      this.stopRunActive = false
      releaseStopRun()
    }

    if (!gotRepl && this.deviceConnected !== Targets.UNCONNECTED) {
      // Interrupts exhausted with no REPL prompt: the device is wedged and the
      // user's only recourse is physical. Say so, rather than silently showing
      // a runnable state on a device that can't run anything.
      const deviceName = this.deviceData[this.deviceConnected]?.displayName || 'Device'
      this.onDebugChange({ deviceUnresponsive: true, deviceName })
    }

    if (this.deviceConnected !== Targets.UNCONNECTED) {
      // Guarded so a disconnect during the loop above can't overwrite the
      // DISCONNECTED mode set by onDeviceDisconnected()
      this.onModeChange(TargetModes.REPL)
    }
    this.onStateChange(TargetStates.STOPPED)
    return gotRepl
  }

  debug = async (filename, code, language, initialBreakpoints) => {
    if (this.deviceConnected === Targets.UNCONNECTED) {
      return
    }
    // TODO check with connect/disconnect tracking to prevent debug() being called
    // The following became necessary once we added multi-tab support
    this.filename = filename
    // The following are kept solely so we can support the RESTART DEBUG button
    this.code = code
    this.initialBreakpoints = initialBreakpoints

    if (this.startPending) {
      return  // A run/debug is already starting; don't launch a second download
    }
    this.startPending = true
    try {
      await this.waitForStopRun()
      this.onModeChange(TargetModes.DEBUG)
      // See run(): disarm post-connect boot watchers before LOADING
      this.clearConnectionTimeout()
      this.endInitializationState()
      this.onStateChange(TargetStates.LOADING)

      editorController.clearMarkers()
      editorController.setDebugModel()

      try {
        await this.load(filename, code)
      } catch (error) {
        // load() has already notified the user and reset mode/state
        return
      }
      this.sendAllBreakpoints()
      this.sendDebuggerCmd('w\r') // step into

      this.onStateChange(TargetStates.AWAITING_INPUT) // TODO Should this be TargetStates.RUNNING?
    } finally {
      this.startPending = false
    }
  }

  debugCommands = (debugCommand) => {
    // console.log(debugCommand)

    switch (debugCommand) {
      case DebugCommands.CONTINUE: {
        this.onStateChange(TargetStates.AWAITING_INPUT)
        this.sendDebuggerCmd('c\r')
        this.onStateChange(TargetStates.RUNNING)
        break
      }
      case DebugCommands.PAUSE: {
        // TODO: right now there is no pause insdie the physical devices
        this.KeyboardInterrupt()
        this.onModeChange(TargetModes.REPL)
        this.onStateChange(TargetStates.STOPPED)
        break
      }
      case DebugCommands.STEP_OVER: {
        this.sendDebuggerCmd('n\r')
        this.onStateChange(TargetStates.RUNNING)
        break
      }
      case DebugCommands.STEP_INTO: {
        if (this.targetMode === TargetModes.REPL && this.lastTargetState === TargetStates.AWAITING_INPUT) {
          this.sendDebuggerCmd('w\r')
        } else if (this.targetMode === TargetModes.DEBUG && this.lastTargetState === TargetStates.AWAITING_INPUT) {
          this.sendDebuggerCmd('s\r')
        }
        this.onStateChange(TargetStates.RUNNING)
        break
      }
      case DebugCommands.STEP_OUT: {
        this.sendDebuggerCmd('r\r')
        this.onStateChange(TargetStates.RUNNING)
        break
      }
      case DebugCommands.RESTART: {
        this.restartDebug()
        break
      }
      case DebugCommands.STOP: {
        this.stopDebug()
        break
      }
      default: {
        throw new Error('Unhandled action type')
      }
    }
  }

  checkVersion = () => {
    if (this.deviceConnected === Targets.UNCONNECTED) {
      return
    }

    if (!this.deviceVersionInfo?.ver || !this.deviceVersionInfo?.date) {
      return
    }

    const activePack = getActiveMissionPack()
    if (!activePack || !activePack?.targetFirmware) {
      return
    }

    // If the connected device isn't used in the pack we don't need to version check.
    let packTargetFirmwareDateStr = activePack?.targetFirmware[this.deviceConnected]
    if (!packTargetFirmwareDateStr) {
      return
    }

    if (packTargetFirmwareDateStr !== '*' && new Date(packTargetFirmwareDateStr) <= new Date(this.deviceVersionInfo.date)) {
      return
    }

    const ver = this.deviceData[this.deviceConnected].hwVer
    const needsUpgrade = !this.deviceVersionInfo.ver || (ver && !this.deviceVersionInfo.ver.includes(ver)) // Assume okay if failed to fetch online version record
    if (needsUpgrade && !this.showedUpgradeNotice) {
      // console.log('device needs upgrade')
      this.showedUpgradeNotice = true
      this.upgradeDeviceCb(this.deviceConnected, this.deviceData[this.deviceConnected].displayName)
    }
  }

  // Track state of serial data stream - from debugger vs REPL/stdout
  trackDebugVsRepl = (strBuf) => {
    /* Note that depending on the speed of the computer, multiple messages can be received in a single buffer
    payload, therefore it is required that we recurse through the buffer until the LAST message has been
    identified. Otherwise the order that we check them will influence the current state.
    */
    this.accumulatedBuf += strBuf
    if (this.accumulatedBuf.length > RX_TRACKING_BUF_SIZE) {
      // The buffer only shrinks on a regex match, so spew that never matches
      // (e.g. print output with embedded data escapes) would grow it without
      // bound — and every rx chunk re-scans the whole buffer, locking up the
      // browser. Keep only the tail; all tokens matched here are far smaller.
      this.accumulatedBuf = this.accumulatedBuf.slice(-RX_TRACKING_BUF_SIZE)
    }
    // console.log(`--ACCUM_BUF--\n${this.accumulatedBuf}\n--END--`)
    let regExMatch = false

    // Code finished running message options (MP-Complete is for CB2)
    const codeDoneRegEx = /Code done running.|MP-Complete/gm
    const codeDoneMatch = codeDoneRegEx.exec(this.accumulatedBuf)
    if (codeDoneMatch) {
      regExMatch = true
      this.accumulatedBuf = this.accumulatedBuf.slice(0, codeDoneMatch.index) + this.accumulatedBuf.slice(codeDoneRegEx.lastIndex)

      // Note: Duplicated in stopRun() -- TODO: consolidate this.
      if (this.targetMode === TargetModes.DEBUG) {
        this.invokeExistingCallbacksOnly(this.debugCallbacks, { line: -1, variables: { Locals: {}, Globals: {} } })
      }

      this.onModeChange(TargetModes.REPL)
      this.onStateChange(TargetStates.STOPPED)
    }

    this.bootRegEx.lastIndex = 0
    // Groups: 1)hash, 2)date, 3)platform, 4)CPU
    // E.g. "MicroPython 64453a93a on 2019-05-01; 4F126609 CODEBOT_CB2 with STM32L476"
    //                      1^             2^         3^        4^             5^
    const bootMatch = this.bootRegEx.exec(this.accumulatedBuf)
    if (bootMatch) {
      if (this.lastTargetState === TargetStates.INITIALIZING) {
        this.endInitializationState()
        this.clearConnectionTimeout()
      }
      this.deviceVersionInfo = { ver: bootMatch[1], date: bootMatch[2] }
      regExMatch = true
      this.accumulatedBuf = this.accumulatedBuf.slice(0, bootMatch.index) + this.accumulatedBuf.slice(this.bootRegEx.lastIndex)
      this.checkVersion()
    }

    unableToSetBkptRegEx.lastIndex = 0
    const unableToSetBkptMatch = unableToSetBkptRegEx.exec(this.accumulatedBuf)
    if (unableToSetBkptMatch) {
      regExMatch = true
      this.accumulatedBuf = this.accumulatedBuf.slice(0, unableToSetBkptMatch.index) + this.accumulatedBuf.slice(unableToSetBkptRegEx.lastIndex)
      // console.log("Unable to set breakpoint", unableToSetBkptMatch)
      // const line = parseInt(unableToSetBkptMatch[1], 10)
      // TODO: Popup "toast" warning "Breakpoint removed from non-executable line"
      // editorInst.session.clearBreakpoint(line - 1)
      // editorInst.session.addGutterDecoration(line - 1, "invalid-breakpoint")
      // Notify Ace and observers of potential changes.
      // editorInst.session._signal("changeBreakpoint", {})
    }

    // Debugger terminated - we are running!
    terminatedExprRegEx.lastIndex = 0
    const terminatedExprMatch = terminatedExprRegEx.exec(this.accumulatedBuf)
    if (terminatedExprMatch) {
      regExMatch = true
      this.accumulatedBuf = this.accumulatedBuf.slice(0, terminatedExprMatch.index) + this.accumulatedBuf.slice(terminatedExprRegEx.lastIndex)
      // this.setStopped(false)
      // this.setMbState(mb.state.RUNNING)
      // this.hiddenDebugCursor = this.curDebugCursor
      // this.setDebugCursor(null)
    }

    // Got REPL prompt >>>
    replPromptRegEx.lastIndex = 0
    const replMatch = replPromptRegEx.exec(this.accumulatedBuf)
    if (replMatch) {
      regExMatch = true
      this.accumulatedBuf = this.accumulatedBuf.slice(0, replMatch.index) + this.accumulatedBuf.slice(replPromptRegEx.lastIndex)
      this.replSeenOnBoot = true
      if (this.lastTargetState === TargetStates.INITIALIZING) {
        // Device came up idle at its REPL — initialization is done, and no
        // stop sequence is needed (see onDeviceConnected).
        this.clearConnectionTimeout()
        this.endInitializationState()
        this.onModeChange(TargetModes.REPL)
      }
      if (!terminatedExprMatch || replMatch.index > terminatedExprMatch.index) {
        if (this.gotReplPrompt) {
          this.gotReplPrompt()
        }
      }
    }

    debugPromptRegEx.lastIndex = 0
    const debugMatch = debugPromptRegEx.exec(this.accumulatedBuf)
    if (debugMatch) {
      regExMatch = true
      this.accumulatedBuf = this.accumulatedBuf.slice(0, debugMatch.index) + this.accumulatedBuf.slice(debugPromptRegEx.lastIndex)
      if ((!terminatedExprMatch || debugMatch.index > terminatedExprMatch.index) &&
        (!replMatch || debugMatch.index > replMatch.index)) {
        // this.setStopped(false)
        // this.setMbMode(mb.mode.DEBUG)
        // this.setMbState(mb.state.IDLE)
        // if (!this.curDebugCursor && this.hiddenDebugCursor) {
        //  this.setDebugCursor(this.hiddenDebugCursor)
        // }
      }
    }

    // Make sure that we handle multiple (of the same message) in the buffer within a single call
    // to trackDebugVsRepl
    if (regExMatch) {
      this.accumulatedBuf = this.accumulatedBuf.slice(1)
      if (this.accumulatedBuf) {
        // console.log("More accumulated", this.accumulatedBuf)
        this.trackDebugVsRepl('')
      }
    }
    // console.log(`--mbMode=${mb.getModeName(this.mbMode)}, mbState=${mb.getStateName(this.mbState)}, isStopped=${this.isStopped}--`)
  }



  // Respond to the STOP DEBUGGING button
  stopDebug = () => {
    this.stopRun()
  }

  // Respond to the RESTART DEBUGGING button
  restartDebug = () => {
    this.stopDebug()
    this.debug(this.filename, this.code, 'python', this.initialBreakpoints)
  }

  checkForTraceback = (strBuf) => {
    this.tracebackBuffer += strBuf

    // If multiple tracebacks exist, process only the first one
    if (this.tracebackBuffer.includes('Traceback')) {
      let firstTracebackPos = this.tracebackBuffer.indexOf('Traceback')
      let secondTracebackPos = this.tracebackBuffer.indexOf('Traceback', firstTracebackPos + 9)
      let tempBuffer = ''
      if (secondTracebackPos > 0) {
        tempBuffer = this.tracebackBuffer.slice(firstTracebackPos, secondTracebackPos)
      } else {
        tempBuffer = this.tracebackBuffer.slice(firstTracebackPos)
      }

      tracebackRegEx.lastIndex = 0
      const m = tracebackRegEx.exec(tempBuffer)
      const isReadOnlyFS = m?.[3].includes('Read-only filesystem')

      if (m !== null && (isReadOnlyFS || m[1] !== '<stdin>') && m[3].indexOf('KeyboardInterrupt') === -1) {
        let filename = m[1]
        let lineNum = m[2]
        let err_msg = m[3]

        if (isReadOnlyFS) {
          err_msg = `${err_msg}\nRESET your ${this.deviceData[this.deviceConnected].displayName} to exit read-only mode.`
        } else if (filename !== '__main__' && filename !== 'main.py') {
          // Append point-of-error filename and line number to message
          err_msg = `${err_msg}\n (${filename} line ${lineNum})`

          // Find last instance of 'main' module in traceback, and set line_number there.
          const traceStackEntryRegEx = /File "(__main__|main\.py)", line (\d*)/gm
          const traceArray = [...tempBuffer.matchAll(traceStackEntryRegEx)]
          lineNum = traceArray?.[traceArray.length - 1]?.[2] ?? '1'
        }

        let errParts = err_msg.split(':')
        const errName = errParts[0]
        errParts.shift()
        const msg = errParts.join(':')


        let err = {
          module: '',
          name: errName,
          lineNumber: parseInt(lineNum, 10),
          message: msg.trim(),
          callStack: null,
          column: 0,
        }

        this.tracebackBuffer = ''

        this.invokeExistingCallbacksOnly(this.userProgramErrorCallbacks, err)
        // this.stopRun()
      }
    }

    if (this.tracebackBuffer.length > TRACEBACK_BUF_SIZE) {
      this.tracebackBuffer = this.tracebackBuffer.substring(this.tracebackBuffer.length - TRACEBACK_BUF_SIZE)
    }
  }

  // Filter debugger-specific output from console buffer (prior to sending to ConsoleRepl)
  filterConsole = (buf) => {
    // Expect to be called on fragments of debugger output stream, so run bytewise state machine.
    let outBuf = ''
    for (let i = 0; i < buf.length; i++) {
      const c = buf[i]
      if (this.consoleFilterState === CF_STATE_ON) {
        if (c === REPL_ESC_DEBUG) {
          this.consoleFilterState = CF_STATE_ON_ESC
        } else if (c === REPL_EDATA_HDR[0]) {
          this.edataHdr = c
          this.consoleFilterState = CF_STATE_EDAT_HDR
        } else {
          outBuf += c
        }
      } else if (this.consoleFilterState === CF_STATE_EDAT_HDR) {
        this.edataHdr += c
        if (this.edataHdr.length === 4) {
          if (this.edataHdr === REPL_EDATA_HDR) {
            // Next 4 bytes are U32 length
            this.consoleFilterState = CF_STATE_EDAT_LEN
            this.edataHdr = ''  // Next accumulate 4-bytes of LEN32
          } else {
            outBuf += this.edataHdr
            this.consoleFilterState = CF_STATE_ON
          }
        }
      } else if (this.consoleFilterState === CF_STATE_EDAT_LEN) {
        this.edataHdr += c
        if (this.edataHdr.length === 4) {
          const str = this.edataHdr
          // Convert to U32 length
          this.edataLen = (str.charCodeAt(0) << 24) |
            (str.charCodeAt(1) << 16) |
            (str.charCodeAt(2) << 8) |
            str.charCodeAt(3)
          this.edataLen >>>= 0  // ensure unsigned
          // Embedded commands can be encoded in length field
          if (this.checkSpecialEdata(this.edataLen)) {
            this.edataLen = 0
            this.consoleFilterState = CF_STATE_ON
          } else {
            this.edataBinArray = []
            this.consoleFilterState = CF_STATE_EDAT_BIN
            this.unackedBytes = 0 // init flow control counter

            // Timeout configuration: during edata transfer, err if no new byte is received within timeout_ms.
            this.finalEdataLen = this.edataLen
            this.timeout_ms = 2000
            this.lastByteTime = Date.now()

            if (this.timer) {
              clearInterval(this.timer)
              this.timer = null
            }

            // Set up a timer to check for incoming data timeout.
            this.timer = setInterval(() => {
              if (this.consoleFilterState !== CF_STATE_EDAT_BIN) {
                return
              }
              const elapsed = Date.now() - this.lastByteTime
              if (elapsed > this.timeout_ms) {
                clearInterval(this.timer)
                this.timer = null

                // Flag error to EdataViewer
                const debugInfo = { edata: { seq: 0, data: null, endOfMessageReached: true } }
                this.onDebugChange(debugInfo)

                // Re-enable normal console
                this.consoleFilterState = CF_STATE_ON
              }
            }, 100)
          }
        }
      } else if (this.consoleFilterState === CF_STATE_EDAT_BIN) {
        this.lastByteTime = Date.now()

        // Dispatch progress every 16 bytes for UI feedback
        if (this.edataBinArray.length === 16) {
          this.edataSeq++
          const debugInfo = { edata: { seq: this.edataSeq, data: this.edataBinArray.join(''), endOfMessageReached: false } }
          this.onDebugChange(debugInfo)  // Dispatch embedded data
        }

        this.edataBinArray.push(c)
        this.edataLen--

        // sending ack every 1024 bytes
        const CHUNK_SIZE = 1024
        this.unackedBytes = (this.unackedBytes || 0) + 1
        if (this.unackedBytes >= CHUNK_SIZE) {
          this.serialComms.send('\x06') // ACK (0x06)
          this.unackedBytes -= CHUNK_SIZE
        }

        if (this.edataLen === 0) {
          const edataBin = this.edataBinArray.join('')
          const debugInfo = { edata: { seq: this.edataSeq, data: edataBin, endOfMessageReached: true } }
          this.onDebugChange(debugInfo)  // Dispatch embedded data
          this.consoleFilterState = CF_STATE_ON
          this.unackedBytes = 0
          clearInterval(this.timer)
          this.timer = null
        }
      } else if (this.consoleFilterState === CF_STATE_ON_ESC) {
        if (c === REPL_ENTER_DEBUG) {
          this.consoleFilterState = CF_STATE_OFF
        } else if (c === REPL_VAR_PREFIX) {
          this.consoleFilterState = CF_STATE_OFF
        } else {
          outBuf += REPL_ESC_DEBUG + c
          this.consoleFilterState = CF_STATE_ON
        }
      } else if (this.consoleFilterState === CF_STATE_OFF) {
        if (c === REPL_ESC_DEBUG) {
          this.consoleFilterState = CF_STATE_OFF_ESC
        }
      } else if (this.consoleFilterState === CF_STATE_OFF_ESC) {
        if (c === REPL_EXIT_DEBUG) {
          if (this.consoleFilterDebugRecurse > 0) {
            this.consoleFilterDebugRecurse--
            this.consoleFilterState = CF_STATE_OFF
          } else {
            this.consoleFilterState = CF_STATE_ON
          }
        } else if (c === REPL_ENTER_DEBUG) {
          this.consoleFilterState = CF_STATE_OFF
          // this.consoleFilterDebugRecurse++
        } else if (c === REPL_VAR_PREFIX) {
          this.consoleFilterState = CF_STATE_OFF
        } else {
          this.consoleFilterState = CF_STATE_OFF
        }
      } else if (this.consoleFilterState === CF_STATE_NO_ECHO) {
        if (this.echoFilter.length === 0) {
          this.consoleFilterState = CF_STATE_ON
          this.echoFilter = ''
        } else {
          this.echoFilter = this.echoFilter.substring(1)
        }
      }
    }

    return outBuf
  }

  // Check embedded data length field for special commands
  checkSpecialEdata = (edataLength32) => {
    const hibyte = (edataLength32 >> 24) & 0xFF
    if (hibyte === 0x01) {
      // This is a "ping" request from the device. Send reply.
      this.serialComms.send('OK\r')
      return true
    }
    return false
  }

  // Characters received from serial port (echoed commands + responses)
  handleSerialChars = (buf) => {
    let strBuf = String.fromCharCode(...buf)
    // console.log(`(${strBuf.length}) '${strBuf}'`)

    this.handleCommandResponseRx(strBuf)

    // stopRun()'s REPL-prompt watcher. Runs unconditionally — unlike
    // trackDebugVsRepl below, which is gated off during LOADING — because
    // stopRun also cleans up after an aborted download while the state is
    // still LOADING, and must be able to see the prompt its ^C elicits.
    if (this.gotReplPrompt) {
      this.replWatchBuf += strBuf
      if (this.replWatchBuf.includes('\n>>> ')) {
        this.replWatchBuf = ''
        this.gotReplPrompt()
      } else {
        this.replWatchBuf = this.replWatchBuf.slice(-8)  // Retain enough tail to span chunks
      }
    }

    // Tracebacks
    this.checkForTraceback(strBuf)

    if (this.lastTargetState !== TargetStates.LOADING) {
      // Variables Panel
      if (this.targetMode === TargetModes.DEBUG) {
        this.varsWatcher.check(strBuf)
      }

      // State
      this.trackDebugVsRepl(strBuf)

      // Console output
      const filteredBuf = this.filterConsole(strBuf)
      validatorController.handleConsoleMsg(filteredBuf)
      if (this.webTerminal?.terminalIO?.print) {
        this.webTerminal.onOutgoingCharacters(filteredBuf)
        // this.webTerminal.onOutgoingCharacters(strBuf)
      }
    }
  }
}
